import axios, { AxiosRequestConfig } from 'axios';
import { message } from 'antd';
import type { ITPath } from 'react-i18next';
import i18n from '~~locales/i18n';
import { getToken, deleteToken } from '~~utils/Auth';
import AppConfig from '~~utils/Config';

const token = getToken();
const instance = axios.create({
  baseURL: AppConfig.API_HOST,
  headers: token ? { authorization: token } : {},
});

type IApiResponse = {
  url: string,
  controller: AbortController,
};
let responseQueue: Array<IApiResponse> = []; // 存是否還有任務未回應
let errorQueue: Array<string> = []; // 錯誤顯示message的API

type IPost = <T>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
export const post:IPost = (url, payload, config) => instance.post(url, payload, config);

type ICancelPost = <T>(url: string, data?: any, config?: AxiosRequestConfig) => {
  send: () => Promise<T>, controller: AbortController
};
export const cancelPost:ICancelPost = (url, payload, config) => {
  const controller = new AbortController();
  return {
    send: () => instance.post(url, payload, { ...config, signal: controller.signal }),
    controller,
  };
};
export const errorPost:IPost = (url, payload, config) => {
  errorQueue.push(url);
  return instance.post(url, payload, config);
};

export const setAPIToken = (jwt: string) => {
  instance.defaults.headers.common.authorization = jwt;
};

instance.interceptors.request.use((config) => {
  if (responseQueue.length > 0) {
    responseQueue = responseQueue.filter((data) => {
      if (data.url !== config.url) return true;
      data.controller.abort();
      return false;
    });
  }
  if (config.signal) return config;
  const controller = new AbortController();
  if (config.url) {
    responseQueue.push({
      url: config.url,
      controller,
    });
  }
  return {
    ...config,
    signal: controller.signal,
  };
});

export class CustomError<T extends Record<string, any>> extends Error {
  errorCode: number;

  errorKey: ITPath;

  errorData: T;

  constructor(errorCode: number, errorKey: ITPath, errorData: T) {
    super(errorKey);
    this.errorCode = errorCode;
    this.errorKey = errorKey;
    this.errorData = errorData;
  }
}

instance.interceptors.response.use((response) => {
  responseQueue = responseQueue.filter((data) => data.url !== response.config.url);
  return response.data;
}, (error) => {
  if (error.message === 'canceled') return null;
  let statusCode = 500;
  let errorKey: ITPath = 'API_ERROR.SYSTEM_ERROR';
  let errorData = { status_code: 500, defaultValue: '' };
  if (error) {
    if (error.response) {
      statusCode = error.response.status;
      const { data } = error.response;
      if (data) {
        if (data.message) errorKey = data.message;
        if (data.data) errorData = data.data;
      } else errorData = { status_code: error.response.status, defaultValue: '' };
      if (error.response.status === 401) {
        message.error(i18n.t(`API_ERROR.${data as 'TOKEN_EXPIRED' | 'NO_SESSION'}`));
        responseQueue.forEach((item) => {
          item.controller.abort();
        });
        responseQueue = [];
        deleteToken();
        window.location.replace('');
        return false;
      }
    } else if (error.request) {
      statusCode = error.request.status;
    }

    if (error.config) {
      responseQueue = responseQueue.filter((item) => item.url !== error.config.url);
      errorQueue = errorQueue.filter((url) => {
        if (url !== error.config.url) return true;
        if (i18n.exists(errorKey)) message.error(i18n.t(errorKey, errorData));
        else message.error(errorKey);
        return false;
      });
    }
  }

  return Promise.reject(new CustomError(statusCode, errorKey, errorData));
});

export default { post, cancelPost, errorPost, setAPIToken };
